import React from 'react';
import { store } from '../store';
import { getText } from './index';

const formatRiskColumns = (formattedRisk, sourceData, emptyColumns=[]) => {
  const { instance } = store.getState();
  const { enabled_languages: enabledLanguages } = instance.instanceConfig;
  const englishWorkPhaseKey = "work_phase_or_ambient_exposure";
  const englishActivityKey = "activity";
  const englishHazardKey = "hazard";
  const englishUnwantedEventKey = "unwanted_event";
  for (const language of enabledLanguages) {
    if (language === "English") {
      formattedRisk[englishWorkPhaseKey] = emptyColumns.includes('work_phase') ? '' : sourceData[englishWorkPhaseKey];
      formattedRisk[englishActivityKey] = emptyColumns.includes('activity') ? '' : sourceData[englishActivityKey];
      formattedRisk[englishHazardKey] = emptyColumns.includes('hazard') ? '' : sourceData[englishHazardKey];
      formattedRisk[englishUnwantedEventKey] = emptyColumns.includes('unwanted_event') ? '' : sourceData[englishUnwantedEventKey];
    } else {
      const languageNameLower = language.toLowerCase();
      const workPhaseKey = `work_phase_${languageNameLower}`;
      const activityKey = `activity_${languageNameLower}`;
      const hazardKey = `hazard_${languageNameLower}`;
      const unwantedEventKey = `unwanted_event_${languageNameLower}`;

      formattedRisk[workPhaseKey] = emptyColumns.includes('work_phase') ? '' : (sourceData[workPhaseKey] || sourceData[englishWorkPhaseKey]);
      formattedRisk[activityKey] = emptyColumns.includes('activity') ? '' : (sourceData[activityKey] || sourceData[englishActivityKey]);
      formattedRisk[hazardKey] = emptyColumns.includes('hazard') ? '' : (sourceData[hazardKey] || sourceData[englishHazardKey]);
      formattedRisk[unwantedEventKey] = emptyColumns.includes('unwanted_event') ? '' : (sourceData[unwantedEventKey] || sourceData[englishUnwantedEventKey]);
    }
  }

  return formattedRisk
}

const createNewRiskEntry = ({ selectedRisk, emptyColumns, column }) => {
  const { language } = store.getState();
  const { name: languageName } = language.language;

  let { id=null, hidden, is_custom=null, template_flags={} } = selectedRisk || {};
  let newEntry = {
    hidden,
    id,
    is_custom,
    column,
    languageName,
    template_flags
  }

  return formatRiskColumns(newEntry, selectedRisk, emptyColumns)
}

const initNewWorkPhaseEntry = (selectedRisk) => {
  const emptyColumns = ['work_phase', 'activity', 'hazard', 'unwanted_event'];
  const column = 'work_phase';
  return createNewRiskEntry({ selectedRisk, emptyColumns, column })
}

const initNewActivityEntry = (selectedRisk) => {
  const emptyColumns = ['activity', 'hazard', 'unwanted_event'];
  const column = 'activity';
  return createNewRiskEntry({ selectedRisk, emptyColumns, column })
}

const initNewHazardEntry = (selectedRisk) => {
  const emptyColumns = ['hazard', 'unwanted_event'];
  const column = 'hazard';
  return createNewRiskEntry({ selectedRisk, emptyColumns, column })
}

const initNewUnwantedEventEntry = (selectedRisk) => {
  const emptyColumns = ['unwanted_event'];
  const column = 'unwanted_event';
  return createNewRiskEntry({ selectedRisk, emptyColumns, column })
}

const updateUnwantedEventColumns = (value, metadata) => {
  const { instance } = store.getState();
  const { enabled_languages: enabledLanguages } = instance.instanceConfig;
  for (const language of enabledLanguages) {
    if (language === "English") {
      metadata["unwanted_event"] = value;
    } else {
      metadata[`unwanted_event_${language.toLowerCase()}`] = value;
    }
  }

  return metadata
}

const initMetadata = () => {
  const riskColumns = formatRiskColumns({}, {});

  return {
    ...riskColumns,
    risk_description: "",
    consequences_description: "",
    highest_rating: "",
    actions: "",
    manager: "",
    effectiveness: "",
    status: { dueDate: null, status: null },
    effectiveness2: "",
    consequences: [
      { key: "financial", value: null },
      { key: "safety", value: null },
      { key: "health", value: null },
      { key: "environmental", value: null },
      { key: "reputation", value: null },
      { key: "legal", value: null },
      { key: "community", value: null },
    ],
    new_consequences: [
      { key: "new_financial", value: null },
      { key: "new_safety", value: null },
      { key: "new_health", value: null },
      { key: "new_environmental", value: null },
      { key: "new_reputation", value: null },
      { key: "new_legal", value: null },
      { key: "new_community", value: null },
    ],
    likelihood: { type: "", value: "" },
    new_likelihood: { type: "", value: "" },
  };
}

const NA_LIKELIHOOD_VALUES = ['NA', 'N/A', '(N/A) Not Applicable'];

const isNALikelihood = (value) => {
  if (value == null || value === 0 || value === '') return false;
  return NA_LIKELIHOOD_VALUES.includes(String(value).trim());
};

const formatLikelihood = (key, metadata) => {
  const value = metadata && metadata[key] && metadata[key].value;
  if (value == null || value === 0 || value === '') return null;
  const trimmed = String(value).trim();
  return trimmed || null;
};

const formatConsequences = (new_risk, consequences) => {
  if (!consequences) return;
  consequences.forEach(consequence => {
    new_risk[consequence.key] = consequence.value || '';
  });
};

const getLikelihoodValue = (row, isFinal, isFiveByFiveMatrix) => {
  const key = `${isFiveByFiveMatrix ? '' : '6x6_'}${isFinal ? 'new_' : ''}likelihood`;
  return row[key];
};

const getFiveByFiveRatingObject = (riskRating, likelihoodValue, riskRatingDict) => {
  if (!riskRating || !likelihoodValue) return null;

  const isNA = isNALikelihood(likelihoodValue);
  const rating = isNA ? 0 : parseInt(riskRating, 10);
  const likelihood = isNA ? 'N/A' : likelihoodValue;

  return (riskRatingDict && riskRatingDict[likelihood]) ? riskRatingDict[likelihood][rating] : null;
};

const fiveByFiveRiskRatingFormatter = (riskRating, likelihoodValue, riskRatingDict) => {
  const ratingObj = getFiveByFiveRatingObject(riskRating, likelihoodValue, riskRatingDict);
  const label = (ratingObj && ratingObj.label) ? ratingObj.label : '';
  return (
    <span>
      { label && <span>{ label } </span> }
      { riskRating > 0 && <span>({ riskRating })</span> }
    </span>
  );
};

const fiveByFiveRiskRatingClasses = (riskRating, likelihoodValue, riskRatingDict) => {
  const ratingObj = getFiveByFiveRatingObject(riskRating, likelihoodValue, riskRatingDict);
  const ratingClass = (ratingObj && ratingObj.class) ? ratingObj.class : '';
  return `${ratingClass} column-color-risk-rating`.trim();
};

const getSixBySixRatingObject = (riskRating, likelihoodValue, sixBySixRiskMatrix) => {
  if (!riskRating || !likelihoodValue || !sixBySixRiskMatrix || sixBySixRiskMatrix.length === 0) return null;

  const likelihood = sixBySixRiskMatrix.find((l) => l.likelihood_rating === likelihoodValue);
  return likelihood ? likelihood.risk_ratings.find((r) => r.risk_rating === riskRating) : null;
};

const sixBySixRiskRatingFormatter = (riskRating, likelihoodValue, sixBySixRiskMatrix) => {
  const ratingObj = getSixBySixRatingObject(riskRating, likelihoodValue, sixBySixRiskMatrix);
  const riskLevel = (ratingObj && ratingObj.risk_level) ? getText(ratingObj.risk_level) : '';
  return <span>{riskLevel}</span>;
};

const sixBySixRiskRatingClasses = (riskRating, likelihoodValue, sixBySixRiskMatrix) => {
  const ratingObj = getSixBySixRatingObject(riskRating, likelihoodValue, sixBySixRiskMatrix);
  const ratingClass = (ratingObj && ratingObj.class) ? ratingObj.class : '';
  return `${ratingClass} column-color-risk-rating`.trim();
};

const riskRatingFormatter = (cell, row, { isFinal=false, isFiveByFiveMatrix, riskRatingDict, sixBySixRiskMatrix }) => {
  const likelihoodValue = getLikelihoodValue(row, isFinal, isFiveByFiveMatrix);

  return isFiveByFiveMatrix ?
    fiveByFiveRiskRatingFormatter(cell, likelihoodValue, riskRatingDict) :
    sixBySixRiskRatingFormatter(cell, likelihoodValue, sixBySixRiskMatrix);
};

const riskRatingClasses = (cell, row, { isFinal=false, isFiveByFiveMatrix, likelihoodConversionToView, riskRatingDict, sixBySixRiskMatrix }) => {
  const likelihoodValue = getLikelihoodValue(row, isFinal, isFiveByFiveMatrix);

  return isFiveByFiveMatrix ?
    fiveByFiveRiskRatingClasses(cell, likelihoodValue, riskRatingDict) :
    sixBySixRiskRatingClasses(cell, likelihoodValue, sixBySixRiskMatrix);
};

const likelihoodCellFormatter = (cell, row, rowIndex, likelihoodOptions) => {
  const value = (cell && typeof cell === 'object' && cell.value) ? cell.value : cell;
  const matchedLikelihood = likelihoodOptions ? likelihoodOptions.find(opt => opt.value === value) : null;
  const displayValue = matchedLikelihood ? matchedLikelihood.text : value;

  return (
    <span className='cell-style' cell={displayValue} row={rowIndex}>
      {displayValue}
    </span>
  );
};

const formatRisk = (risk) => {
  // 1. Single Parse: Metadata is usually the heaviest part
  let metadata = {};
  try {
    metadata = typeof risk.metadata === 'string' ? JSON.parse(risk.metadata) : (risk.metadata || {});
  } catch (e) {
    metadata = {};
  }

  // 2. Pre-calculate common fields to avoid re-calculation in render
  // We spread the risk and metadata, then overwrite with formatted values
  let formattedRisk = { 
    ...risk, 
    ...metadata, 
    metadata, // Keep the object reference for reference-heavy components
    activityGroup: risk.name, 
    inspections: risk.inspections 
  };

  // 3. Batched Formatting (Ensure these helpers are optimized)
  formattedRisk = formatRiskColumns(formattedRisk, metadata);

  // Likelihood Mapping
  const likelihoodKeys = ["likelihood", "new_likelihood", "6x6_likelihood", "6x6_new_likelihood"];
  likelihoodKeys.forEach(key => {
    formattedRisk[key] = formatLikelihood(key, metadata);
  });

  // Consequence Mapping
  const consequenceKeys = ["consequences", "new_consequences", "6x6_consequences", "6x6_new_consequences"];
  consequenceKeys.forEach(key => {
    formatConsequences(formattedRisk, metadata[key]);
  });

  // 4. In-place Control Formatting (Avoids multiple array allocations)
  formattedRisk["existing_preventative"] = [];
  formattedRisk["existing_recovery"] = [];
  formattedRisk["additional_preventative"] = [];
  formattedRisk["additional_recovery"] = [];

  const controls = risk.controls || [];
  for (let i = 0; i < controls.length; i++) {
    const control = controls[i];
    switch (control.control_type) {
      case "existing-preventative": formattedRisk["existing_preventative"].push(control); break;
      case "existing-recovery": formattedRisk["existing_recovery"].push(control); break;
      case "additional-preventative": formattedRisk["additional_preventative"].push(control); break;
      case "additional-recovery": formattedRisk["additional_recovery"].push(control); break;
      default: break;
    }
  }

  return formattedRisk;
};

export {
  initNewWorkPhaseEntry,
  initNewActivityEntry,
  initNewHazardEntry,
  initNewUnwantedEventEntry,
  initMetadata,
  formatRiskColumns,
  updateUnwantedEventColumns,
  isNALikelihood,
  formatLikelihood,
  formatConsequences,
  riskRatingFormatter,
  riskRatingClasses,
  likelihoodCellFormatter,
  formatRisk
}


// WEBPACK FOOTER //
// ./src/helpers/risk_helpers.js